// client/src/writer/hooks/useChapterManagement.ts
import React, { useCallback, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { apiRequest } from '../../lib/queryClient.js';
import { useToast } from '../../hooks/use-toast.js';

import type { ProjectState, Chapter, ChapterVitals, ChapterDetailEdits } from '../types.js';
import { getTargetWordCount } from '../utils/projectUtils.js';
import { initialChapterVitals } from '../constants.js';
import { CoreState } from './useCoreState.js';
import { ModalControls } from './useModalStates.js';
import { ConfirmationLogic } from './useConfirmation.js';

export interface ChapterManagementLogic {
    // handleSelectChapter removed - use the one from CoreState
    handleSaveChapterDetails: () => void; // Moved here as it affects chapter state directly
    navigateVersion: (direction: 'prev' | 'next') => void;
    setChapterStatus: (status: Chapter['status']) => void;
    handleRecalculateChapterWordCounts: () => void;
    handleExecuteSplitChapter: (splitConfig: { numChapters: number; hints: string[] }) => Promise<void>;
    handleToggleChapterForMerge: (chapterId: string) => void;
    getMergeableSequenceInfo: () => { isMergeable: boolean; selectedChapters: Chapter[]; firstIndexInProject: number; lastIndexInProject: number; };
    isChapterSelectionDisabledForMerge: (chapterId: string) => boolean;
    handleOpenMergeModal: () => void;
    handleExecuteMergeChapters: (mergeConfig: { suggestedTitle?: string; instructions?: string }) => Promise<void>;
}

export const useChapterManagement = (
    coreState: CoreState,
    modalControls: ModalControls,
    confirmationLogic: ConfirmationLogic,
): ChapterManagementLogic => {
    const {
        projectState, setProjectState, apiKey, setLoadingState, setError, editor, activeChapter,
    } = coreState;
    const { confirmDiscardChapterDetailChanges } = confirmationLogic;
    const { toast } = useToast();

    // REMOVED: useChapterState - now using the one from CoreState to eliminate duplication
    const { activeChapterDetailEdits, setActiveChapterDetailEdits, areChapterDetailsCurrentlyUnsaved } = coreState;

interface ChapterOutlineItem {
    id: string;
    title: string;
    summary: string;
    plotOutline?: string;
    geography?: string;
    charactersInvolved?: string;
    characterActions?: string;
    soulOfChapter?: string;
    vitals?: ChapterVitals;
}

    const aiMutation = useMutation({
        mutationFn: async ({ task, payload }: { task: string, payload: any }) => {
            const response = await apiRequest(`/api/llm/${task}`, "POST", payload);
            return response.json();
        },
        onSuccess: (data: any, variables: { task: string, payload: any }) => {
            const { task, payload } = variables;
            let successMessage = "AI action successful!";
            
            if (task === 'chapter-split') {
                const newChapterOutlines = data.data;
                const originalWordCountPerNewChapter = (modalControls.chapterToSplit?.expectedWordCount ?? 0) > 0 && newChapterOutlines.length > 0 ? Math.round((modalControls.chapterToSplit?.expectedWordCount ?? 0) / newChapterOutlines.length) : 500;

                const newSplitChapters: Chapter[] = newChapterOutlines.map((item: ChapterOutlineItem, index: number) => ({
                    id: item.id,
                    projectId: projectState?.projectId || '',
                    title: item.title,
                    summary: item.summary,
                    content: '', // Start with empty content
                    order: (modalControls.chapterToSplit?.order ?? 0) + index,
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                    plotOutline: item.plotOutline || '',
                    geography: item.geography || '',
                    charactersInvolved: item.charactersInvolved || '',
                    characterActions: item.characterActions || '',
                    soulOfChapter: item.soulOfChapter || '',
                    expectedWordCount: originalWordCountPerNewChapter,
                    status: 'draft',
                    history: [],
                    historyIndex: -1,
                    vitals: item.vitals ? { ...initialChapterVitals, ...item.vitals } : { ...initialChapterVitals },
                    previewContent: null,
                    previewSummary: null,
                    previewWordCountTarget: 0,
                }));

                setProjectState((prev: ProjectState | null) => {
                    if (!prev) return null;
                    const chapters = prev.chapters ?? [];
                    const chapterIndex = chapters.findIndex((ch: Chapter) => ch.id === modalControls.chapterToSplit!.id);
                    if (chapterIndex === -1) return { ...prev, isUnsaved: true };
                    const updatedChapters = [...chapters];
                    updatedChapters.splice(chapterIndex, 1, ...newSplitChapters);
                    return { ...prev, chapters: updatedChapters, activeChapterId: newSplitChapters[0]?.id || null, isUnsaved: true, selectedChapterIdsForMerge: [] };
                });
                modalControls.closeSplitChapterModal();
                successMessage = "Chapter split successfully.";
            } else if (task === 'chapter-merge') {
                const newMergedChapterOutline = data.data;
                const totalExpectedWordCount = payload.selectedChapters.reduce((sum: number, ch: Chapter) => sum + (ch.expectedWordCount ?? 0), 0);
                const newMergedChapter: Chapter = {
                    id: newMergedChapterOutline.id,
                    projectId: projectState?.projectId || '',
                    title: newMergedChapterOutline.title,
                    summary: newMergedChapterOutline.summary,
                    content: '', // Start with empty content
                    order: payload.selectedChapters[0]?.order ?? 0,
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                    plotOutline: newMergedChapterOutline.plotOutline || '',
                    geography: newMergedChapterOutline.geography || '',
                    charactersInvolved: newMergedChapterOutline.charactersInvolved || '',
                    characterActions: newMergedChapterOutline.characterActions || '',
                    soulOfChapter: newMergedChapterOutline.soulOfChapter || '',
                    expectedWordCount: totalExpectedWordCount,
                    status: 'draft',
                    history: [],
                    historyIndex: -1,
                    vitals: newMergedChapterOutline.vitals ? { ...initialChapterVitals, ...newMergedChapterOutline.vitals } : { ...initialChapterVitals },
                    previewContent: null,
                    previewSummary: null,
                    previewWordCountTarget: 0,
                };
                setProjectState((prev: ProjectState | null) => {
                    if (!prev) return null;
                    const chapters = prev.chapters ?? [];
                    const updatedChapters = [...chapters];
                    updatedChapters.splice(payload.firstIndexInProject, payload.selectedChapters.length, newMergedChapter);
                    return { ...prev, chapters: updatedChapters, activeChapterId: newMergedChapter.id, isUnsaved: true, selectedChapterIdsForMerge: [] };
                });
                modalControls.closeMergeModal();
                successMessage = "Chapters merged successfully.";
            }
            toast({ title: "Success", description: successMessage });
        },
        onError: (error: any, variables: { task: string, payload: any }) => {
            setError(`AI Error: ${error.message}`);
            toast({ title: "AI Error", description: error.message, variant: "destructive" });
            if (error.rawResponse) modalControls.openDebugResponseModal(error.rawResponse);
        },
        onSettled: (data: any, error: any, variables: { task: string, payload: any }) => {
            const { task } = variables;
            if (task === 'chapter-split') setLoadingState('splitChapter', false);
            else if (task === 'chapter-merge') setLoadingState('mergeChapter', false);
        }
    });


    // REMOVED: Duplicate handleSelectChapter function - use the one from useCoreState instead
    // This eliminates the competing source of truth and sync issues

    const handleSaveChapterDetails = useCallback(async () => {
        if (!activeChapter || !activeChapterDetailEdits) return;
        
        // First update the local state
        setProjectState((prev: ProjectState | null) => {
            if (!prev) return null;
            return {
            ...prev,
            chapters: (prev.chapters ?? []).map((c: Chapter) =>
                c.id === activeChapter.id ? {
                    ...c,
                    title: activeChapterDetailEdits.title ?? c.title,
                    summary: activeChapterDetailEdits.summary ?? c.summary,
                    geography: activeChapterDetailEdits.geography ?? c.geography,
                    charactersInvolved: activeChapterDetailEdits.charactersInvolved ?? c.charactersInvolved,
                    characterActions: activeChapterDetailEdits.characterActions ?? c.characterActions,
                    soulOfChapter: activeChapterDetailEdits.soulOfChapter ?? c.soulOfChapter,
                    expectedWordCount: parseInt(activeChapterDetailEdits.expectedWordCount || (c.expectedWordCount ?? 0).toString(), 10) || c.expectedWordCount,
                    vitals: activeChapterDetailEdits.vitals ? { ...activeChapterDetailEdits.vitals } : c.vitals,
                } : c
            ), isUnsaved: false, // Mark as saved since we're saving to database
        }});
        
        // Now save to database immediately
        if (projectState && projectState.projectId) {
            try {
                setLoadingState('save', true);
                await apiRequest(`/api/story-projects/${projectState.projectId}`, 'PUT', {
                    projectData: { 
                        ...projectState, 
                        chapters: (projectState?.chapters ?? []).map((c: Chapter) =>
                            c.id === activeChapter.id ? {
                                ...c,
                                title: activeChapterDetailEdits.title ?? c.title,
                                summary: activeChapterDetailEdits.summary ?? c.summary,
                                geography: activeChapterDetailEdits.geography ?? c.geography,
                                charactersInvolved: activeChapterDetailEdits.charactersInvolved ?? c.charactersInvolved,
                                characterActions: activeChapterDetailEdits.characterActions ?? c.characterActions,
                                soulOfChapter: activeChapterDetailEdits.soulOfChapter ?? c.soulOfChapter,
                                expectedWordCount: parseInt(activeChapterDetailEdits.expectedWordCount || (c.expectedWordCount ?? 0).toString(), 10) || c.expectedWordCount,
                                vitals: activeChapterDetailEdits.vitals ? { ...activeChapterDetailEdits.vitals } : c.vitals,
                            } : c
                        ),
                        isUnsaved: false 
                    },
                    title: projectState.currentProjectName || projectState.outlineIdea.substring(0, 50),
                    description: projectState.outlineIdea,
                    bookType: projectState.bookMetadata?.genreSubject || 'NOVEL',
                    targetWordCount: getTargetWordCount(coreState.dbProject),
                    status: 'active',
                });
                toast({ title: "Success", description: "Chapter details saved successfully!" });
            } catch (error: any) {
                setError(`Save Error: ${error.message}`);
                toast({ title: "Error", description: `Failed to save chapter details: ${error.message}`, variant: "destructive" });
                // Mark as unsaved if save failed
                setProjectState((prev: ProjectState | null) => prev ? { ...prev, isUnsaved: true } : null);
            } finally {
                setLoadingState('save', false);
            }
        } else {
            toast({ title: "Success", description: "Chapter details updated. Remember to 'Save Project'." });
        }
    }, [activeChapter, activeChapterDetailEdits, setProjectState, setLoadingState, setError, toast, projectState]);

    const navigateVersion = useCallback((direction: 'prev' | 'next') => {
        if (!activeChapter) return;
        setProjectState((prev: ProjectState | null) => {
            if (!prev) return null;
            const chapters = prev.chapters ?? [];
            const chap = chapters.find((c: Chapter) => c.id === activeChapter.id);
            if (!chap || !chap.history || typeof chap.historyIndex !== 'number') return prev;

            let newIndex = chap.historyIndex;
            if (direction === 'prev' && chap.historyIndex > 0) {
                newIndex--;
            }
            if (direction === 'next' && chap.historyIndex < chap.history.length - 1) {
                newIndex++;
            }

            if (newIndex !== chap.historyIndex) {
                return {
                    ...prev,
                    chapters: (prev.chapters ?? []).map((c: Chapter) =>
                        c.id === activeChapter.id ? { ...c, historyIndex: newIndex } : c
                    ),
                    isUnsaved: true,
                };
            }
            return prev;
        });
    }, [activeChapter, setProjectState]);

    const setChapterStatus = useCallback((status: Chapter['status']) => {
        if (!activeChapter) return;
        setProjectState((prev: ProjectState | null) => {
            if (!prev) return null;
            return {
            ...prev,
            chapters: (prev.chapters ?? []).map((c: Chapter) => c.id === activeChapter.id ? { ...c, status } : c),
            isUnsaved: true,
        }});
    }, [activeChapter, setProjectState]);

    const handleRecalculateChapterWordCounts = useCallback(() => {
        if (!projectState) return;
        const totalTarget = getTargetWordCount(coreState.dbProject);
        const chapters = projectState?.chapters ?? [];
        if (typeof totalTarget !== 'number' || totalTarget <= 0 || chapters.length === 0) { setError("Set Target Novel Word Count and ensure chapters exist."); return; }
        if (!window.confirm("Overwrite expected word count for all chapters?")) return;
        const wordsPerChapter = Math.round(totalTarget / chapters.length);
        setProjectState((prev: ProjectState | null) => {
            if (!prev) return null;
            return { ...prev, chapters: (prev.chapters ?? []).map((ch: Chapter) => ({ ...ch, expectedWordCount: wordsPerChapter })), isUnsaved: true }
        });
        toast({ title: "Success", description: "Chapter word counts recalculated." });
    }, [projectState, setProjectState, setError, toast]);

    const handleExecuteSplitChapter = useCallback(async (splitConfig: { numChapters: number; hints: string[] }) => {
        if (!apiKey) { setError("Set API Key."); modalControls.openApiKeyModal(apiKey); return; }
        if (!modalControls.chapterToSplit) { setError("No chapter selected for splitting."); return; }
        if (!projectState) return;

        setLoadingState('splitChapter', true); setError(null);
        const originalChapterDetails = {
            id: modalControls.chapterToSplit.id, title: modalControls.chapterToSplit.title, summary: modalControls.chapterToSplit.summary,
            geography: modalControls.chapterToSplit.geography, charactersInvolved: modalControls.chapterToSplit.charactersInvolved,
            characterActions: modalControls.chapterToSplit.characterActions, soulOfChapter: modalControls.chapterToSplit.soulOfChapter,
            vitals: modalControls.chapterToSplit.vitals,
        };
        aiMutation.mutate({
            task: 'chapter-split',
            payload: {
                originalChapterDetails,
                numChapters: splitConfig.numChapters,
                hints: splitConfig.hints,
                outlineIdea: projectState.outlineIdea,
                bookMetadata: projectState.bookMetadata,
                worldBuildingData: projectState.blueprint?.worldBuildingSuggestions,
            }
        });
    }, [apiKey, modalControls, setLoadingState, setError, aiMutation, projectState]);

    const handleToggleChapterForMerge = useCallback((chapterId: string) => {
        setProjectState((prev: ProjectState | null) => {
            if (!prev) return null;
            const newSelection = prev.selectedChapterIdsForMerge.includes(chapterId) ? prev.selectedChapterIdsForMerge.filter((id: string) => id !== chapterId) : [...prev.selectedChapterIdsForMerge, chapterId];
            const chapters = prev.chapters ?? [];
            const sortedSelection = chapters.filter((ch: Chapter) => newSelection.includes(ch.id)).map((ch: Chapter) => ch.id);
            return { ...prev, selectedChapterIdsForMerge: sortedSelection, isUnsaved: true };
        });
    }, [setProjectState]);

    const getMergeableSequenceInfo = useCallback(() => {
        if (!projectState) return { isMergeable: false, selectedChapters: [], firstIndexInProject: -1, lastIndexInProject: -1 };
        const chapters = projectState.chapters ?? [];
        const selectedChapterIdsForMerge = projectState.selectedChapterIdsForMerge ?? [];
        if (selectedChapterIdsForMerge.length < 2) return { isMergeable: false, selectedChapters: [], firstIndexInProject: -1, lastIndexInProject: -1 };
        const selectedChaps: Chapter[] = [];
        selectedChapterIdsForMerge.forEach((id: string) => { const chap = chapters.find((ch: Chapter) => ch.id === id); if (chap) selectedChaps.push(chap); });
        if (selectedChaps.length !== selectedChapterIdsForMerge.length) return { isMergeable: false, selectedChapters: [], firstIndexInProject: -1, lastIndexInProject: -1 };
        const sortedSelectedChapsWithIndices = selectedChaps.map((ch: Chapter) => ({ ch, index: chapters.findIndex((c: Chapter) => c.id === ch.id) })).sort((a, b) => a.index - b.index);
        let isSequential = true;
        for (let i = 0; i < sortedSelectedChapsWithIndices.length - 1; i++) { if (sortedSelectedChapsWithIndices[i].index + 1 !== sortedSelectedChapsWithIndices[i+1].index) { isSequential = false; break; } }
        if (isSequential && sortedSelectedChapsWithIndices.length >=2) { return { isMergeable: true, selectedChapters: sortedSelectedChapsWithIndices.map((item: { ch: Chapter; index: number }) => item.ch), firstIndexInProject: sortedSelectedChapsWithIndices[0].index, lastIndexInProject: sortedSelectedChapsWithIndices[sortedSelectedChapsWithIndices.length - 1].index }; }
        return { isMergeable: false, selectedChapters: [], firstIndexInProject: -1, lastIndexInProject: -1 };
    }, [projectState]);

    const isChapterSelectionDisabledForMerge = useCallback((chapterId: string): boolean => {
        if (!projectState) return true;
        const { chapters, selectedChapterIdsForMerge } = projectState;
        if (selectedChapterIdsForMerge.length === 0) return false;
        const sequenceInfo = getMergeableSequenceInfo();
        if (selectedChapterIdsForMerge.includes(chapterId)) return false;
        
        if (sequenceInfo.isMergeable && sequenceInfo.selectedChapters.length > 0 && typeof sequenceInfo.firstIndexInProject === 'number' && typeof sequenceInfo.lastIndexInProject === 'number') {
            const currentChapterIndex = chapters?.findIndex((ch: Chapter) => ch.id === chapterId) ?? -1;
            if (currentChapterIndex === sequenceInfo.firstIndexInProject - 1 || currentChapterIndex === sequenceInfo.lastIndexInProject + 1) return false;
            return true;
        } else if (selectedChapterIdsForMerge.length === 1) {
            const singleSelectedIndex = chapters?.findIndex((ch: Chapter) => ch.id === selectedChapterIdsForMerge[0]) ?? -1;
            const currentChapterIndex = chapters?.findIndex((ch: Chapter) => ch.id === chapterId) ?? -1;
            if (currentChapterIndex === singleSelectedIndex - 1 || currentChapterIndex === singleSelectedIndex + 1) return false;
            return true;
        }
        return true;
    }, [projectState, getMergeableSequenceInfo]);

    const handleOpenMergeModal = useCallback(() => {
        const { isMergeable, selectedChapters } = getMergeableSequenceInfo();
        if (isMergeable && selectedChapters.length >= 2) {
             // Confirmation is handled by caller (confirmOverallDiscardChanges)
            modalControls.openMergeModal(selectedChapters);
        } else { setError("Select two or more sequential chapters to merge."); }
    }, [getMergeableSequenceInfo, modalControls, setError]);
    
    const handleExecuteMergeChapters = useCallback(async (mergeConfig: { suggestedTitle?: string; instructions?: string }) => {
        if (!apiKey) { setError("Set API Key."); modalControls.openApiKeyModal(apiKey); return; }
        const { isMergeable, selectedChapters, firstIndexInProject } = getMergeableSequenceInfo();
        if (!isMergeable || selectedChapters.length < 2 || typeof firstIndexInProject !== 'number') { setError("Invalid selection for merging."); modalControls.closeMergeModal(); return; }
        if (!projectState) return;
        setLoadingState('mergeChapter', true); setError(null);
        const chapterDetailsToMerge = selectedChapters.map((ch: Chapter) => ({ 
            title: ch.title, summary: ch.summary, geography: ch.geography, 
            charactersInvolved: ch.charactersInvolved, characterActions: ch.characterActions, 
            soulOfChapter: ch.soulOfChapter, vitals: ch.vitals 
        }));
        aiMutation.mutate({
            task: 'chapter-merge',
            payload: {
                chapterDetailsToMerge,
                suggestedTitle: mergeConfig.suggestedTitle,
                instructions: mergeConfig.instructions,
                selectedChapters, // Pass for onSuccess to calculate totalExpectedWordCount
                firstIndexInProject, // Pass for onSuccess to splice correctly
                outlineIdea: projectState.outlineIdea,
                bookMetadata: projectState.bookMetadata,
                worldBuildingData: projectState.blueprint?.worldBuildingSuggestions,
            }
        });
    }, [apiKey, getMergeableSequenceInfo, setLoadingState, setError, aiMutation, modalControls, projectState]);

    return {
        // handleSelectChapter removed - use coreState.handleSelectChapter instead
        handleSaveChapterDetails,
        navigateVersion,
        setChapterStatus,
        handleRecalculateChapterWordCounts,
        handleExecuteSplitChapter,
        handleToggleChapterForMerge,
        getMergeableSequenceInfo,
        isChapterSelectionDisabledForMerge,
        handleOpenMergeModal,
        handleExecuteMergeChapters,
    };
};
